import { useState, useLayoutEffect, useCallback } from 'react';
import { accrualFragment } from '../../services/bank-transactions/fragments';
import { gqlClient } from '../../shared/gqlClient';
import { EnrichedBankTransaction } from '../../types';
import { useQuery } from '@tanstack/react-query';
import { CategorizationResult } from '.';
import { getClassificationFromCategorizationInput } from '../BankTransactions/BankTransactionsTable';
import { BankTransactionClassificationInput } from '../../__generated-global__';

export const checkHasAccrual = async (companyId: string, transactionId: EnrichedBankTransaction['id']) => {
  const { bankTransaction } = await gqlClient.query({
    __name: 'bankTransaction',
    bankTransaction: {
      accrual: accrualFragment,
      __args: {
        getBankTransactionInput: {
          transactionId,
          companyId,
        },
      },
    },
  });
  return Boolean(bankTransaction?.accrual?.id);
};

const getBusinessEventDefinitions = async (companyId: string) => {
  const { getBusinessEventsAccrualsConfig } = await gqlClient.query({
    __name: 'getBusinessEventsAccrualsConfig',
    getBusinessEventsAccrualsConfig: {
      __args: {
        input: {
          companyId,
        },
      },
      event: {
        id: true,
      },
      // isRelevantToAccruals: true,
      isRelevantToAccrualsFixedAsset: true,
    },
  });
  return getBusinessEventsAccrualsConfig;
};

export const useIsDisabledByAccrual = (
  companyId: string,
  transactionId?: EnrichedBankTransaction['id'],
  categorizationResult?: CategorizationResult,
) => {
  const [isAccrualExists, setIsAccrualExists] = useState(true);
  const [hasFixedAssetBusinessEvent, setHasFixedAssetBusinessEvent] = useState(false);
  const [errorMessageAccrual, setErrorMessageAccrual] = useState<string>();
  const businessEventDefinitionResult = useQuery({
    queryKey: ['getBusinessEventDefinitions', companyId],
    queryFn: () => getBusinessEventDefinitions(companyId),
  });

  const checkHasFixedAssetBusinessEvent = useCallback(
    (classifications: BankTransactionClassificationInput[]) => {
      if (!businessEventDefinitionResult.data) {
        return;
      }
      const businessEvents = classifications
        .map((c) => c.businessEvent)
        .filter((event): event is string => {
          const isEventValid = typeof event === 'string';
          if (!isEventValid) {
            console.error('Invalid business event', { companyId, transactionId, event });
          }
          return isEventValid;
        });
      const availableFixedAssetBusinessEvents = businessEventDefinitionResult.data.flatMap(
        ({ event: { id }, isRelevantToAccrualsFixedAsset }) => (isRelevantToAccrualsFixedAsset ? id : []),
      );
      const hasFixedAssetBusinessEvent = businessEvents.some((event) =>
        availableFixedAssetBusinessEvents.includes(event),
      );
      setHasFixedAssetBusinessEvent(hasFixedAssetBusinessEvent);
      return hasFixedAssetBusinessEvent;
    },
    [businessEventDefinitionResult.data, companyId, transactionId],
  );

  useLayoutEffect(() => {
    if (transactionId) {
      checkHasAccrual(companyId, transactionId).then(setIsAccrualExists);
    }
    return () => {
      setIsAccrualExists(true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionId]);

  useLayoutEffect(() => {
    if (categorizationResult && businessEventDefinitionResult.data) {
      const classifications = getClassificationFromCategorizationInput(categorizationResult, true);
      if (!classifications) {
        return;
      }
      checkHasFixedAssetBusinessEvent(classifications);
    }
  }, [businessEventDefinitionResult.data, categorizationResult, checkHasFixedAssetBusinessEvent]);

  useLayoutEffect(() => {
    if (isAccrualExists) {
      setErrorMessageAccrual('This action is blocked because the transaction has been spread');
    }
    if (hasFixedAssetBusinessEvent && !isAccrualExists) {
      setErrorMessageAccrual('Transactions can be classified to fixed asset categories from the main app only');
    }
    return () => {
      setErrorMessageAccrual(undefined);
    };
  }, [isAccrualExists, hasFixedAssetBusinessEvent]);

  return {
    isAccrualExists,
    setIsAccrualExists,
    checkHasFixedAssetBusinessEvent,
    setHasFixedAssetBusinessEvent,
    errorMessageAccrual,
  };
};
